// src/components/molecules/PortalUserModal/PortalUserModal.tsx
import React, { useMemo, useState } from 'react';
import { Container, Row, Col, Form, RadioGroup, Label } from 'shared-portal-component';
import { z } from 'zod';

import AppModal from '@/components/common/AppModal/AppModal';
import type { PortalUserType, PortalUserValues, SeatAvailability } from '@/types/users.types';

interface PortalUserModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: PortalUserValues) => void;

  title: string;
  submitText?: string;
  modalSize?: 'sm' | 'md' | 'lg';
  className?: string;

  initialValues?: Partial<PortalUserValues>;
  deptOrgOptions?: string[];

  /** Per-type seat exhaustion. Only enforced when `enforceSeatLimit` is true. */
  seatFull?: SeatAvailability;
  /** Whether seat data has loaded; the limit is only applied once it has (fail open). */
  seatsReady?: boolean;
  /** Apply the seat limit (create flow only — editing never consumes a new seat). */
  enforceSeatLimit?: boolean;
}

/* ============================
   Schema
   userType is owned by the modal (not the schema-driven Form) so the body can
   react to the selected type — the shared Form exposes no field-watch hook.
============================ */

const MIN_LENGTH = 1;
const portalUserSchema = z.object({
  name: z.string().min(MIN_LENGTH, 'Please enter a full name'),
  email: z.string().min(MIN_LENGTH, 'Email is required').email('Please enter a valid email'),
  phone: z.string().min(MIN_LENGTH, 'Phone number is required'),
  deptOrg: z.string().min(MIN_LENGTH, 'Please select a department / org'),
  sendInvite: z.boolean().default(true),
  forcePwdReset: z.boolean().default(true),
});

type PortalUserFormValues = z.infer<typeof portalUserSchema>;

/* ============================
   Defaults
============================ */
const DEFAULT_SUBMIT = 'Save';
const DEFAULT_USER_TYPE: PortalUserType = 'Internal';
const DEFAULT_FORM_VALUES: PortalUserFormValues = {
  name: '',
  email: '',
  phone: '',
  deptOrg: '',
  sendInvite: true,
  forcePwdReset: true,
};
const DEFAULT_DEPTS = ['HR', 'IT', 'Delivery', 'Legal', 'Sales'];

const USER_TYPE_OPTIONS: Array<{ label: string; value: PortalUserType }> = [
  { label: 'Internal', value: 'Internal' },
  { label: 'External', value: 'External' },
];

const mergeFormDefaults = (seed?: Partial<PortalUserValues>): PortalUserFormValues => ({
  ...DEFAULT_FORM_VALUES,
  ...(seed === undefined
    ? {}
    : {
        name: seed.name ?? DEFAULT_FORM_VALUES.name,
        email: seed.email ?? DEFAULT_FORM_VALUES.email,
        phone: seed.phone ?? DEFAULT_FORM_VALUES.phone,
        deptOrg: seed.deptOrg ?? DEFAULT_FORM_VALUES.deptOrg,
        sendInvite: seed.sendInvite ?? DEFAULT_FORM_VALUES.sendInvite,
        forcePwdReset: seed.forcePwdReset ?? DEFAULT_FORM_VALUES.forcePwdReset,
      }),
});

/* ============================
   Component
============================ */
const PortalUserModal: React.FC<PortalUserModalProps> = ({
  open,
  onClose,
  onSubmit,
  title,
  submitText = DEFAULT_SUBMIT,
  modalSize = 'sm',
  className = '',
  initialValues,
  deptOrgOptions = DEFAULT_DEPTS,
  seatFull,
  seatsReady = false,
  enforceSeatLimit = false,
}) => {
  const formDefaults = useMemo(() => mergeFormDefaults(initialValues), [initialValues]);

  const [userType, setUserType] = useState<PortalUserType>(
    initialValues?.userType ?? DEFAULT_USER_TYPE
  );

  const fields = useMemo(
    () => [
      {
        name: 'name',
        label: 'User Name',
        placeholder: 'Full name',
        required: true,
        type: 'text' as const,
      },
      {
        name: 'email',
        label: 'User Email',
        placeholder: 'user@company.com',
        required: true,
        type: 'email' as const,
      },
      {
        name: 'phone',
        label: 'Phone Number',
        placeholder: '(555) 123-4567',
        required: true,
        type: 'phone' as const,
      },
      {
        name: 'deptOrg',
        label: 'Dept / Org',
        placeholder: 'Select department / org',
        required: true,
        type: 'select' as const,
        options: deptOrgOptions.map((d) => ({ label: d, value: d })),
      },
    ],
    [deptOrgOptions]
  );

  const handleSubmit = (values: PortalUserFormValues) => {
    onSubmit({ ...values, userType });
  };

  // Only block when the limit is enforced, seat data has loaded, and the
  // currently selected type is full. Unloaded/failed seat data → never block.
  const isSelectedTypeFull = enforceSeatLimit && seatsReady && seatFull?.[userType] === true;

  return open ? (
    <AppModal
      onClose={onClose}
      title={title}
      size={modalSize}
      className={className}
      aria-label={title}
    >
      <Container fluid>
        <Row spacing="none" justify="center">
          <Col>
            <div className="mb-4">
              <Label htmlFor="userType">Is this user Internal or External?</Label>
              <RadioGroup
                name="userType"
                value={userType}
                options={USER_TYPE_OPTIONS}
                size="sm"
                onChange={(value: string) => {
                  if (value === 'Internal' || value === 'External') {
                    setUserType(value);
                  }
                }}
              />
            </div>

            {/* Reserve the form's vertical space so swapping in the shorter
                seat-limit message doesn't shrink the modal. */}
            <div className="flex min-h-[360px] flex-col justify-center">
              {isSelectedTypeFull ? (
                <p className="text-left" role="alert">
                  You&apos;ve reached your seat limit for {userType} users. Please delete or make
                  inactive existing users or contact your plan administrator to add seats.
                </p>
              ) : (
                <Form
                  schema={portalUserSchema}
                  defaultValues={formDefaults}
                  onSubmit={handleSubmit}
                  submitText={submitText}
                  fields={fields}
                />
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </AppModal>
  ) : null;
};

export default PortalUserModal;
